import React, {useContext, useState} from 'react';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Visibility from '@mui/icons-material/Visibility';
import {Button, CircularProgress, IconButton, InputAdornment, Link} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import {useForm} from 'react-hook-form';
import {FormContainer, TextFieldElement} from 'react-hook-form-mui';
import {useLocation, useNavigate, useSearchParams} from 'react-router-dom';
import { useMutation, useQueryClient } from "@tanstack/react-query";

import {isAdmin} from "@lib/auth";
import {axiosRequest} from "@lib/axios";
import UserContext from '@lib/contexts/userContext';
import {PageContext} from '@lib/contexts/pageContext';

const LoginForm = () => {
  const navigate = useNavigate();
  const { setPageMessage, setPageErrorMessage } = useContext(PageContext);
  const { signIn } = useContext(UserContext);

  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const location = useLocation();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const {
    handleSubmit,
    control,
    setValue,
  } = useForm({
    mode: 'onSubmit',
  });

  const signInMutation = useMutation({
    mutationFn: async ({email, password}) => axiosRequest({
      method: 'POST',
      url: 'auth/login',
      data: {
        email,
        password,
      },
      anonymous: true,
    }),
    onSuccess: async (result) => {
      // console.log('[signInMutation] result', result);
      const decodedTokens = signIn(result);

      // Invalidate all queries cached in RQ
      await queryClient.invalidateQueries();

      setSubmitting(false);
      // TODO: Refactor this into a shared hook, so SSO login can also use it.
      let redirect = searchParams.get('redirect');
      if (!redirect) {
        redirect = location.state?.redirect;
      }
      if (redirect) {
        navigate(redirect);
      } else if (isAdmin(decodedTokens.idToken['cognito:groups'])) {
        navigate('/member/profile');
      } else if (result.auth.user.is_sso) {
        navigate('/events');
      } else {
        navigate('/member/activities');
      }
      // End thing to refactor
    },
    onError: (err) => {
      console.log('[signInMutation] error:', err);
      setSubmitting(false);
      // TODO(auth): customize message if auth sent new verification email
      setPageErrorMessage('Error logging in!', err);
    },
  });

  const handleLogin = async ({ email, password }) => {
    if (!submitting) {
      setSubmitting(true);
      setPageErrorMessage('');
      try {
        await signInMutation.mutateAsync({email, password});
      } catch (e) {
        console.log('[LoginForm][handleLogin] API error:', e);
      }
    }
  };

  return (
    <FormContainer onSuccess={handleSubmit(handleLogin)} FormProps={{ className: 'mui' }}>
      <Grid container spacing={3}>
        {submitting && (
          <Grid xs={12} display="flex" justifyContent="center">
            <CircularProgress/>
          </Grid>
        )}
        {!submitting && (
          <>
            <Grid xs={12}>
              <TextFieldElement
                id="field-email"
                name="email"
                label={'Email'}
                control={control}
                autoComplete="email"
                required
                fullWidth
                placeholder="Enter your email here"
              />
            </Grid>
            <Grid xs={12}>
              <TextFieldElement
                id="field-password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                label={'Password'}
                control={control}
                placeholder="Enter your password here"
                autoComplete="current-password"
                required
                fullWidth
                InputProps={{
                  endAdornment:
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        // onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff/> : <Visibility/>}
                      </IconButton>
                    </InputAdornment>
                }}
              />
            </Grid>
          </>
        )}
        <Grid xs={12}>
          <Button
            id="button-signin"
            variant="contained"
            size="small"
            type="submit"
            disabled={submitting}
            fullWidth
            sx={{ marginTop: '20px' }}
          >
            Sign In
          </Button>
        </Grid>
        <Grid xs={12} display="flex" justifyContent={'center'}>
          <Link
            id="button-forgot"
            component={'button'}
            variant="body1"
            color="secondary"
            underline="none"
            sx={{ marginTop: '10px' }}
            onClick={() => {
              navigate('/login/password');
            }}
          >
            Forgot your password?
          </Link>
        </Grid>
      </Grid>
    </FormContainer>
  );
};

export default LoginForm;
